import { useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import LoadingButton from 'components/common/LoadingButton';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormProvider,
  RHFControl,
  RHFDatePicker,
  RHFDocumentUpload,
  RHFSelect
} from 'components/common/form';
import Flex from 'components/common/Flex';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const schema = Yup.object().shape({
  inspectionDate: Yup.string().required('Required Field'),
  reviewDate: Yup.string().required('Required Field'),
  hospital: Yup.string().required('Required Field'),
  doctorName: Yup.string(),
  comment: Yup.string(),
  reportDocument: Yup.mixed()
});

function AddReportModal({
  show,
  onHide,
  title,
  loading,
  onSubmit,
  initValues,
  hospitalOptions
}) {
  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      inspectionDate: format(new Date(), 'yyyy-MM-dd'),
      reviewDate: '',
      hospital: '',
      doctorName: '',
      comment: '',
      reportDocument: [],
      editReport: null
    },
    resolver: yupResolver(schema)
  });

  const { setValue, handleSubmit, reset, watch } = methods;
  const reportDocument = watch('reportDocument');
  const editReport = watch('editReport');

  useEffect(() => {
    if (initValues) {
      reset({
        inspectionDate: initValues.inspection_date,
        reviewDate: initValues.review_date,
        hospital: initValues.hosptial,
        doctorName: initValues.doctor_name || '',
        comment: initValues.medical_note || '',
        reportDocument: [],
        editReport: initValues.medical_document?.[0] || null
      });
    }
  }, [initValues, reset]);

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton className="modal-header">
        <Modal.Title className="modal-header-title">{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pb-3 ps-3 pe-3">
        <FormProvider
          methods={methods}
          onSubmit={handleSubmit(onSubmit)}
          loading={loading}
        >
          <Flex gap="2" direction="column" className="mt-3">
            <RHFDatePicker name="inspectionDate" label="Inspection Date" required />
            <RHFDatePicker name="reviewDate" label="Review Date" required />
            <RHFSelect
              label="Hospital"
              name="hospital"
              options={hospitalOptions}
              required
            />
            <RHFControl
              label="Doctor Name"
              name="doctorName"
              placeholder="Doctor name"
              type="text"
            />
            <RHFControl
              label="Medical Notes"
              name="comment"
              placeholder="Notes"
              type="text"
              as="textarea"
              rows={3}
            />
            <RHFDocumentUpload
              label="Report PDF"
              accept=".pdf"
              name="reportDocument"
              multiple={false}
              maxFiles={1}
              maxSizeMB={10}
            />
            {editReport && (!reportDocument || reportDocument.length === 0) && (
              <div>
                <a
                  href={editReport.url}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FontAwesomeIcon icon="link" />
                  <span className="ms-2">{editReport.name}</span>
                </a>
                <Button
                  variant="danger"
                  size="sm"
                  className="ms-2"
                  onClick={() => setValue('editReport', null)}
                >
                  delete
                </Button>
              </div>
            )}
            <LoadingButton
              style={{ width: '100%', color: 'white' }}
              variant="primary"
              loading={loading}
              disabled={loading}
              type="submit"
            >
              {initValues ? 'Edit' : 'Save'}
            </LoadingButton>
          </Flex>
        </FormProvider>
      </Modal.Body>
    </Modal>
  );
}

AddReportModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  title: PropTypes.string,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  hospitalOptions: PropTypes.array,
  initValues: PropTypes.object
};

export default AddReportModal;
